import React from 'react';
import { css } from '@emotion/css';
import { format, isBefore } from 'date-fns';
import { Link } from 'is24-corecss';
import { IconS24ChevronRight24 } from '@is24/cosma-ui-icons';
import SchufaCheckJpg from './images/schufa-check.jpg';
import SchufaCheckWebp from './images/schufa-check.webp';
import SchufaCheckJpgMobile from './images/schufa-check-mobile.jpg';
import SchufaCheckWebpMobile from './images/schufa-check-mobile.webp';
import PaperAndPenPng from './images/paper-and-pen.png';
import PaperAndPenWebp from './images/paper-and-pen.webp';
import LetterPng from './images/letter.png';
import LetterWebp from './images/letter.webp';
import DownloadPng from './images/download.png';
import DownloadWebp from './images/download.webp';
import Is24SolvencyPreview from '../solvencyModal/is24_solvency_check_document_preview.png';
import { Benefit } from '../../../../../@types/benefits';
import { BenefitModalProps } from '../../../../../@types/benefitModalProps';

import { OrderedList } from '../../../../common/display/orderedList/OrderedList';
import { ImageSource } from '../../../../common/display/imageSource/ImageSource';
import { useGetHasProduct } from '../../../../../customHooks/useGetHasProduct.hook';
import { ProductType } from '../../../../../@types/product';
import { useGetIs24SolvencyStatus, useGetSchufaStatus } from '../../../../../apiHooks/solvencyStatus.hook';
import schufaImage1 from './images/image@1x.jpg';
import schufaImage2 from './images/image@2x.jpg';
import schufaImage3 from './images/image@3x.jpg';
import { SolvencyChoiceModal } from '../solvencyModal/SolvencyChoiceModal';
import { SolvencyDocument } from './components/SolvencyDocument';
import { SchufaModal } from './components/SchufaModal';
import { Msg, useIntl } from '@/i18n';
import { APP_CONFIG } from '@/config';
import useOptimizelyExperimentVariation from '@/optimizely/useOptimizelyExperimentVariation';

const styles = {
  overviewTab: {
    maxWidth: '530px',
    withTwoButtons: {
      '@media (max-width: 420px)': {
        marginBottom: '52px',
      },
    },
  },
} as const;

const OverviewTab = () => {
  const { schufaStatus } = useGetSchufaStatus();
  const { is24SolvencyStatus } = useGetIs24SolvencyStatus();

  const isEntitledPlusRent = useGetHasProduct(ProductType.MieterPlus);
  const todayDate = new Date(Date.now());

  const hasSchufa = schufaStatus?.verificationCode && schufaStatus?.expirationDate;
  const hasIs24Solvency = is24SolvencyStatus?.verificationCode && is24SolvencyStatus?.expirationDate;

  if (hasIs24Solvency || hasSchufa) {
    const is24SolvencyExpirationDate = is24SolvencyStatus?.expirationDate
      ? new Date(is24SolvencyStatus?.expirationDate)
      : null;
    const isIs24SolvencyActive = is24SolvencyExpirationDate ? isBefore(todayDate, is24SolvencyExpirationDate) : false;

    const schufaExpirationDate = new Date(schufaStatus?.expirationDate!);
    const isSchufaActive = schufaExpirationDate ? isBefore(todayDate, schufaExpirationDate!) : false;

    return (
      <>
        {hasIs24Solvency && (
          <SolvencyDocument
            isActive={isIs24SolvencyActive}
            formattedDate={format(is24SolvencyExpirationDate!, 'dd.MM.yyyy')}
            preview1={Is24SolvencyPreview}
            preview2={Is24SolvencyPreview}
            preview3={Is24SolvencyPreview}
            verificationCode={is24SolvencyStatus?.verificationCode}
            isEntitledPlusRent={isEntitledPlusRent}
          />
        )}
        {hasIs24Solvency && hasSchufa && <div style={{ height: '32px', width: '100%' }} />}
        {hasSchufa && (
          <SolvencyDocument
            isActive={isSchufaActive}
            formattedDate={format(schufaExpirationDate!, 'dd.MM.yyyy')}
            preview1={schufaImage1}
            preview2={schufaImage2}
            preview3={schufaImage3}
            verificationCode={schufaStatus?.verificationCode}
            isEntitledPlusRent={isEntitledPlusRent}
          />
        )}
      </>
    );
  }

  return (
    <div className={`${css(styles.overviewTab, styles.overviewTab.withTwoButtons)}`}>
      <div className='font-heading-xsmall-regular padding-bottom-l'>
        <Msg
          id='benefits.schufaCheckMieterPlus.modal.overview.paragraph'
          defaultMessage='Original SCHUFA-BonitätsCheck that...'
        />
      </div>
      <ul className='list-check list-spacing-s'>
        <li>
          <Msg
            id='benefits.schufaCheckMieterPlus.modal.overview.bullet1'
            defaultMessage='Download online immediately - even...'
          />
        </li>
        <li>
          <Msg id='benefits.schufaCheckMieterPlus.modal.overview.bullet2' defaultMessage='Order once and...' />
        </li>
        <li>
          <Msg
            id='benefits.schufaCheckMieterPlus.modal.overview.bullet3'
            defaultMessage='Increase the chance of your...'
          />
        </li>
      </ul>
      {!isEntitledPlusRent && (
        <Link
          href={
            '/meinkonto/premium-mitgliedschaft/?cmp_id=10-051528&cmp_name=residential_profile&cmp_position=residential_benefits&cmp_creative=solvency_cta'
          }
          className='margin-top-l'
          variant='default'
          rightIcon={<IconS24ChevronRight24 />}
        >
          <Msg
            id='benefits.schufaCheckMieterPlus.modal.overview.linkButton'
            defaultMessage='Discover Mieter Plus....'
          />
        </Link>
      )}
    </div>
  );
};

const HowItWorksTab = () => {
  const { t } = useIntl();
  const Item = OrderedList.Item;

  return (
    <div className='margin-bottom-xxl'>
      <OrderedList>
        <Item
          image={
            <ImageSource
              image={PaperAndPenPng}
              imageWebp={PaperAndPenWebp}
              imageAlt={t({
                id: 'benefits.schufaCheckMieterPlus.modal.howItWorks.image1',
                defaultMessage: 'paper and pen',
              })}
              sizes='100px'
            />
          }
          text={t({
            id: 'benefits.schufaCheckMieterPlus.modal.howItWorks.paragraph1',
            defaultMessage: 'Enter your details',
          })}
        />
        <Item
          image={
            <ImageSource
              image={LetterPng}
              imageWebp={LetterWebp}
              imageAlt={t({
                id: 'benefits.schufaCheckMieterPlus.modal.howItWorks.image2',
                defaultMessage: 'opened letter',
              })}
              sizes='100px'
            />
          }
          text={t({
            id: 'benefits.schufaCheckMieterPlus.modal.howItWorks.paragraph2',
            defaultMessage: 'get your documents',
          })}
        />
        <Item
          omit={true}
          image={
            <ImageSource
              image={DownloadPng}
              imageWebp={DownloadWebp}
              imageAlt={t({
                id: 'benefits.schufaCheckMieterPlus.modal.howItWorks.image3',
                defaultMessage: 'download',
              })}
              sizes='100px'
            />
          }
          text={t({
            id: 'benefits.schufaCheckMieterPlus.modal.howItWorks.paragraph3',
            defaultMessage: 'or directly download',
          })}
        />
      </OrderedList>
    </div>
  );
};

const ButtonArea = () => {
  const { schufaStatus } = useGetSchufaStatus();
  const { is24SolvencyStatus } = useGetIs24SolvencyStatus();

  const hasSchufa = schufaStatus?.verificationCode && schufaStatus?.expirationDate;
  const hasIs24Solvency = is24SolvencyStatus?.verificationCode && is24SolvencyStatus?.expirationDate;
  const hasDocuments = hasSchufa || hasIs24Solvency;

  const ctaTranslationKey = hasDocuments
    ? 'benefits.schufaCheckMieterPlus.modal.ctaButtonSolvencyDocuments'
    : 'benefits.schufaCheckMieterPlus.modal.ctaButtonNoSolvencyDocuments';
  const ctaDefaultMessage = hasDocuments ? 'Zur Bonitätsauskunft' : 'Zum SCHUFA-BonitätsCheck';

  return (
    <div className='align-center'>
      <a className='button-secondary' href={hasDocuments ? APP_CONFIG.rentProfileUrl : APP_CONFIG.schufaCheckUrl}>
        <Msg id={ctaTranslationKey} defaultMessage={ctaDefaultMessage} />
      </a>
    </div>
  );
};

export const SchufaCheckMieterPlus = ({ isVisible, onClose }: BenefitModalProps) => {
  const { t } = useIntl();
  const isSchufaHighlighted = useOptimizelyExperimentVariation('schufa_highlighted_experiment', 'variation1');

  if (isSchufaHighlighted) {
    return (
      <SchufaModal
        title={t({ id: 'benefits.schufaCheckMieterPlus.headline', defaultMessage: 'SCHUFA-BonitätsCheck' })}
        isVisible={isVisible}
        image={SchufaCheckJpg}
        imageWebp={SchufaCheckWebp}
        imageMobile={SchufaCheckJpgMobile}
        imageMobileWebp={SchufaCheckWebpMobile}
        imageUseFullWidth={true}
        imageAlt={t({
          id: 'benefits.schufaCheckMieterPlus.modal.image',
          defaultMessage: 'SCHUFA=BonitätsCheck paper handover',
        })}
        tabsContent={[
          <OverviewTab key='benefits.schufaCheckMieterPlus.modal.overview' />,
          <HowItWorksTab key='benefits.schufaCheckMieterPlus.modal.howItWorks' />,
        ]}
        benefit={Benefit.SCHUFA}
        onClose={onClose}
      />
    );
  }

  return (
    <SolvencyChoiceModal
      title={t({ id: 'benefits.schufaCheckMieterPlus.headline', defaultMessage: 'SCHUFA-BonitätsCheck' })}
      isVisible={isVisible}
      image={SchufaCheckJpg}
      imageWebp={SchufaCheckWebp}
      imageMobile={SchufaCheckJpgMobile}
      imageMobileWebp={SchufaCheckWebpMobile}
      imageUseFullWidth={true}
      imageAlt={t({
        id: 'benefits.schufaCheckMieterPlus.modal.image',
        defaultMessage: 'SCHUFA=BonitätsCheck paper handover',
      })}
      tabsContent={[
        <OverviewTab key='benefits.schufaCheckMieterPlus.modal.overview' />,
        <HowItWorksTab key='benefits.schufaCheckMieterPlus.modal.howItWorks' />,
      ]}
      buttonArea={<ButtonArea />}
      benefit={Benefit.SCHUFA}
      onClose={onClose}
    />
  );
};
